import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";

export const useSubscription = () => {
    const propertyContext = usePropertyContext();
    const auth = useAuth();

    // Safely extract from contexts
    const properties = propertyContext?.properties || [];
    const propertiesLoading = propertyContext?.loading;
    const userProfile = auth?.userProfile;
    const authLoading = auth?.loading;

    // Derived trial values
    const trialExpiresAt = userProfile?.trial_expires_at;
    const hasUsedTrial = userProfile?.has_used_trial || false;
    const isTrialActive = trialExpiresAt && new Date(trialExpiresAt) > new Date();

    // Derived values
    const plan = userProfile?.subscription_plan || 'free';

    // If trial is active, we grant temp premium + increase house limit to 2
    let maxProperties = userProfile?.max_properties || 1;
    if (plan === 'free') {
        maxProperties = 1;
    }
    if (isTrialActive && maxProperties < 2) {
        maxProperties = 2;
    }

    // Count only properties OWNED by the user for subscription limits
    const ownedProperties = properties?.filter(p => (p.user_id === auth?.user?.id)) || [];
    const ownedPropertyCount = ownedProperties.length;
    const currentPropertyCount = properties?.length || 0; // Keep for other uses if any

    const hasPremium = plan !== 'free' || userProfile?.has_premium_features || isTrialActive;
    const loading = authLoading || propertiesLoading;

    // Only allow adding if we are NOT loading and still under the limit of OWNED properties
    const canAddProperty = !loading && ownedPropertyCount < maxProperties;

    /**
     * Checks if a specific feature is available to the user
     * @param {string} featureName - 'vault_upload', 'maintenance_history', 'sharing'
     * @param {number} count - optional current count of items for tiered features
     */
    const isFeatureLocked = (featureName, count = 0) => {
        if (loading) return false;

        // 1. If user HAS premium (Real Plan or Active Trial), nothing is locked
        if (hasPremium) return false;

        // 2. If user is on FREE tier, check specific restrictions
        const premiumOnlyFeatures = ['analytics', 'priority_support', 'sharing'];
        if (premiumOnlyFeatures.includes(featureName)) return true;

        // Rule: Free tier allows up to 5 vault items
        if (featureName === 'vault_limit' && count >= 5) return true;

        // Rule 1: Allow only 1 service log PER appliance
        if (featureName === 'maintenance_history') return count >= 1;

        // Rule 2: Allow service logs on only 2 appliances TOTAL
        if (featureName === 'maintenance_appliance_limit') return count >= 2;

        // Rule 3: Allow only 2 plants on free tier
        if (featureName === 'plant_limit') return count >= 2;

        return false;
    };

    const getPlanDisplay = () => {
        switch (plan) {
            case 'premium_1': return 'Essential (1 House)';
            case 'premium_3': return 'Investor (3 Houses)';
            case 'premium_5': return 'Professional (5 Houses)';
            case 'enterprise': return 'Estate (Unlimited)';
            default: return 'Free Tier';
        }
    };

    const isPropertyLocked = (propertyId) => {
        if (loading) return false;
        const property = properties.find(p => p.id === propertyId);
        if (!property) return false;

        const isOwner = property.user_id === auth?.user?.id;
        if (!isOwner) return false;

        const ownedProperties = properties.filter(p => p.user_id === auth?.user?.id);
        const ownedIndex = ownedProperties.findIndex(p => p.id === propertyId);
        return ownedIndex >= maxProperties;
    };

    return {
        plan,
        maxProperties,
        currentPropertyCount,
        hasPremium,
        canAddProperty,
        isFeatureLocked,
        isPropertyLocked,
        getPlanDisplay,
        isTrialActive,
        trialExpiresAt,
        hasUsedTrial,
        loading
    };
};
